import moment from "moment-timezone";
import _ from "lodash";
import {
  getIdtrTemplate,
  IDTR_BLANK_PAGES,
} from "../constants/identity.theft.report.constants.js";

// The sheet is laid out in unscaled CSS px and printed/shown with this scale (see the raw css).
export const IDTR_SCALE = 0.6969;
export const IDTR_SHEET_WIDTH_PX = 1138.2;
export const IDTR_SHEET_HEIGHT_PX = 1610.8;
export const IDTR_PAGE_HEIGHT_PX = 1534;

/**
 * Lays out atomic blocks onto pages of a fixed height.
 * blocks: [{ id, height, gap }] in unscaled px. The gap is the top margin of a block,
 * dropped when the block is first on its page (as Chrome does for page breaks).
 * Returns an array of pages, each an array of block ids.
 */
export function paginateBlocks(blocks, pageHeight = IDTR_PAGE_HEIGHT_PX) {
  const pages = [];
  let current = [];
  let used = 0;
  blocks.forEach((block) => {
    const gap = current.length ? block.gap : 0;
    if (current.length && used + gap + block.height > pageHeight) {
      pages.push(current);
      current = [];
      used = 0;
    }
    used += (current.length ? block.gap : 0) + block.height;
    current.push(block.id);
  });
  if (current.length) pages.push(current);
  return pages;
}

export const createAccount = () => ({
  company: "",
  accountNumber: "",
  dateBegan: "",
  dateDiscovered: "",
  amount: "",
});

// Default dates are taken in US Eastern time, same as the backend (config.timeZone).
export const IDTR_TIME_ZONE = "America/New_York";

export const formatPrintedAt = (date = new Date()) =>
  moment(date).tz(IDTR_TIME_ZONE).format("M/D/YY, h:mm A");

export const formatSignatureDate = (date = new Date()) =>
  moment(date).tz(IDTR_TIME_ZONE).format("M/D/YYYY");

const PRINTED_AT_FORMAT = "M/D/YY, h:mm A";
const SIGNATURE_DATE_FORMAT = "M/D/YYYY";

/**
 * The header date (printedAt) and the signature date are the same day in two formats.
 * Both helpers return null while the source value is incomplete, so partial input is left alone.
 */
export function signatureDateFromPrintedAt(printedAt) {
  const m = moment(printedAt, PRINTED_AT_FORMAT, true);
  return m.isValid() ? m.format(SIGNATURE_DATE_FORMAT) : null;
}

export function printedAtWithDate(printedAt, signatureDate) {
  const day = moment(signatureDate, SIGNATURE_DATE_FORMAT, true);
  if (!day.isValid()) return null;
  const current = moment(printedAt, PRINTED_AT_FORMAT, true);
  const time = current.isValid() ? current : moment().tz(IDTR_TIME_ZONE);
  return day
    .hour(time.hour())
    .minute(time.minute())
    .format(PRINTED_AT_FORMAT);
}

export function createForm(templateKey, now = new Date()) {
  const template = getIdtrTemplate(templateKey);
  if (!template) return null;
  return {
    template: template.key,
    header: {printedAt: formatPrintedAt(now)},
    reportNumber: "",
    contact: {
      firstName: "",
      lastName: "",
      // Printed as: street (multi-line) / "City , ST 12345" / USA
      street: "",
      city: "",
      state: "",
      zip: "",
      phone: "",
      email: "",
    },
    // Editable statement body, prefilled from the template; the credit-inquiries one ends with
    // "following:" and a space so the user continues the list right after it.
    statement: template.hasInquiries ? template.statement + " " : template.statement,
    accounts: Array.from({length: template.defaultAccounts}, createAccount),
    inquiries: "",
    signatureDate: formatSignatureDate(now),
    blankPages: IDTR_BLANK_PAGES,
  };
}

const CITY_STATE_ZIP = /^(.*?)\s*,\s*([A-Za-z]{2})\s+(\d{5})(?:-\d{4})?$/;

/**
 * Older prefills carry the address as one multi-line `address` or as street / cityStateZip / country.
 * Splits them into street, city, state and zip; a city line that does not parse stays in the street,
 * so nothing is lost.
 */
function legacyAddressToFields(prefill) {
  const contact = prefill.contact;
  if (!contact || (contact.address == null && contact.cityStateZip == null)) {
    return prefill;
  }
  const lines = (
    contact.address != null
      ? String(contact.address).split("\n")
      : [contact.street, contact.cityStateZip]
  )
    .map((line) => (line || "").trim())
    .filter(Boolean);
  if (lines.length && /^(usa|us|united states)$/i.test(lines[lines.length - 1])) {
    lines.pop();
  }
  const fields = {street: "", city: "", state: "", zip: ""};
  const match = lines.length > 1 && CITY_STATE_ZIP.exec(lines[lines.length - 1]);
  if (match) {
    lines.pop();
    fields.city = match[1];
    fields.state = match[2].toUpperCase();
    fields.zip = match[3];
  }
  fields.street = lines.join("\n");
  const rest = _.omit(contact, ["address", "street", "cityStateZip", "country"]);
  return {...prefill, contact: {...rest, ...fields}};
}

/**
 * Deep-merges a partial form (prefill) into a form. Arrays replace the
 * original array entirely; account items are completed with empty fields.
 */
export function mergePrefill(form, prefill) {
  if (!prefill || typeof prefill !== "object") return _.cloneDeep(form);
  const merged = _.mergeWith(
    _.cloneDeep(form),
    legacyAddressToFields(prefill),
    (objValue, srcValue) => {
      if (Array.isArray(srcValue)) {
        return srcValue.map((item) => ({...createAccount(), ...item}));
      }
      return undefined;
    }
  );
  if (!merged.accounts || !merged.accounts.length) {
    merged.accounts = [createAccount()];
  }
  return merged;
}

export const buildReportTitle = (reportNumber) =>
  `IDTheftReport_OO_${reportNumber || ""}`;

export const buildReportFileName = (reportNumber) =>
  `Dispute Report ${reportNumber || ""}.pdf`;

/**
 * Clones the sheets container (wrapper included, it carries the font) and turns it into static markup:
 * inputs become spans with the same classes and the typed value,
 * contenteditable attributes are dropped, screen-only nodes are removed.
 */
export function exportSheetsHtml(container) {
  const clone = container.cloneNode(true);
  clone.querySelectorAll("[data-screen-only]").forEach((el) => el.remove());
  clone.querySelectorAll("input, textarea").forEach((el) => {
    const span = document.createElement("span");
    span.className = el.className
      .replace(/idtr-input--error|idtr-input--focus/g, "")
      .replace(/\s+/g, " ")
      .trim();
    span.textContent = el.value;
    if (el.getAttribute("style")) {
      span.setAttribute("style", el.getAttribute("style"));
    }
    el.parentNode.replaceChild(span, el);
  });
  clone.querySelectorAll("[contenteditable]").forEach((el) => {
    el.removeAttribute("contenteditable");
    el.removeAttribute("spellcheck");
  });
  clone
    .querySelectorAll("[placeholder]")
    .forEach((el) => el.removeAttribute("placeholder"));
  return clone.outerHTML;
}

/**
 * Saves the PDF in the background, without leaving the page.
 * The blob is typed as a plain binary file: with application/pdf some browsers
 * (Firefox, Safari, Chrome set to open PDFs) show it in their viewer instead of saving it.
 */
export function downloadBase64Pdf(base64, fileName) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], {type: "application/octet-stream"});
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.rel = "noopener";
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked later so a slow save is not cut off.
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// Bureau priority for values taken from the credit report (Dispute Info).
export const IDTR_BUREAU_PRIORITY = ["Equifax", "Experian", "TransUnion"];

const isBlank = (value) =>
  value === undefined || value === null || String(value).trim() === "";

// Amounts of 0 are treated as missing (the report stores 0 when there is no value).
const amountDigits = (value) => {
  if (isBlank(value)) return "";
  const digits = String(value).split(".")[0].replace(/\D/g, "");
  return Number(digits) > 0 ? String(Number(digits)) : "";
};

/** First filled value of `key` across the bureaus, in priority order. */
function pickByPriority(list, key, transform = (v) => (isBlank(v) ? "" : String(v).trim())) {
  const sorted = IDTR_BUREAU_PRIORITY.map((bureau) =>
    (list || []).find((item) => item.bureau === bureau)
  ).filter(Boolean);
  for (const item of sorted) {
    const value = transform(item[key]);
    if (value) return value;
  }
  return "";
}

const monthYear = (value) => {
  if (isBlank(value)) return "";
  const m = moment(value);
  return m.isValid() ? m.format("MM/YYYY") : "";
};

/** Report account (with the per-bureau list) behind an account selected on the Send Letters page. */
export function findReportAccount(report, selected) {
  const accounts = (report && report.accounts) || [];
  return (
    accounts.find(
      (item) =>
        item.name === selected.name &&
        (item.account_id || "") === (selected.account_id || "")
    ) || accounts.find((item) => item.name === selected.name) || null
  );
}

/** Account fields of the report, taken from the Dispute Info of one bank account. */
export function buildIdtrAccount(reportAccount, now = new Date()) {
  const list = (reportAccount && reportAccount.list) || [];
  const amount =
    pickByPriority(list, "high_balance", amountDigits) ||
    pickByPriority(list, "credit_limit", amountDigits);
  return {
    ...createAccount(),
    company: pickByPriority(list, "account_name") || (reportAccount && reportAccount.name) || "",
    accountNumber: pickByPriority(list, "account_number"),
    dateBegan: pickByPriority(list, "date_opened", monthYear),
    dateDiscovered: moment(now).tz(IDTR_TIME_ZONE).format("MM/YYYY"),
    amount,
  };
}

/** Generated email: First Name + Year of Birth + @gmail.com (e.g. Philip1958@gmail.com). */
export function buildIdtrEmail(firstName, birthDate) {
  const name = String(firstName || "").replace(/\s+/g, "");
  if (!name) return "";
  const birth = isBlank(birthDate) ? null : moment(birthDate);
  const year = birth && birth.isValid() ? birth.format("YYYY") : "";
  return `${name}${year}@gmail.com`;
}

/** Prefill of the report from the client and the selected credit report accounts. */
export function buildIdtrPrefill(
  {customer = {}, report = {}, accounts = [], statement = ""},
  now = new Date()
) {
  const reportAccounts = accounts.map((selected) =>
    buildIdtrAccount(findReportAccount(report, selected) || {name: selected.name}, now)
  );
  return {
    contact: {
      firstName: customer.first_name || "",
      lastName: customer.last_name || "",
      street: customer.street || "",
      city: customer.city || "",
      state: String(customer.state || "").toUpperCase(),
      zip: customer.postal_code ? String(customer.postal_code) : "",
      phone: customer.mobile || customer.phone || "",
      email: buildIdtrEmail(customer.first_name, customer.birth_date),
    },
    ...(reportAccounts.length ? {accounts: reportAccounts} : {}),
    // Replaces the standard statement of the template when given.
    ...(statement && statement.trim() ? {statement: statement.trim()} : {}),
  };
}

export function base64PdfToFile(base64, fileName) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], fileName, {type: "application/pdf"});
}

/** Plain text of an html message (the cover letter Message), keeping line breaks. */
export function htmlToPlainText(html) {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(String(html), "text/html");
  doc.querySelectorAll("br").forEach((el) => el.replaceWith("\n"));
  doc
    .querySelectorAll("p, div, li, h1, h2, h3, h4, h5, h6, tr")
    .forEach((el) => el.append("\n"));
  return (doc.body.textContent || "")
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}
